/*
 * OPTIMUS MONETARY PRESENTATION — the one currency formatter both portals use.
 *
 * Every displayed CPM, eCPM and spend figure on Optimus renders through this
 * module: a "$", exactly two decimals with trailing zeros preserved, and
 * normal grouping. $65.41. $25.00. $0.00. $1,250.00.
 *
 * The defect this exists to prevent: a card that printed a raw backend
 * operand with `Number(x).toLocaleString()` put "65.408" on a client screen —
 * neither a currency nor a rounded one — while the card beside it, using its
 * own component-local helper, printed "$65.41". Nine components each owning a
 * private `$` + `toFixed(2)` is nine chances to disagree.
 *
 * ROUNDING IS PRESENTATION ONLY. The backend comparison contract
 * (optimus_comparison_semantics_v1) decides relationship, variance, grade and
 * recommendation from full source precision; these helpers touch nothing but
 * the string a human reads.
 *
 * Kept byte-identical in Cortex (client) and Quantum (superadmin) so the two
 * portals cannot drift into different money.
 */

/* The governed money shape: 2 decimals, trailing zeros kept, grouped. */
export const OPTIMUS_MONEY_FRACTION_DIGITS = 2;

export const OPTIMUS_MONEY_UNAVAILABLE = "$—";

const toFiniteNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.replace(/[$,\s]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/*
 * THE money formatter. `unavailable` names what an absent figure looks like on
 * the calling surface — a KPI tile wants "$—", an inline evidence fragment
 * usually wants null so the whole fragment can be withheld rather than
 * rendered half-empty.
 */
export function formatOptimusMoney(value, { unavailable = OPTIMUS_MONEY_UNAVAILABLE } = {}) {
  const n = toFiniteNumber(value);
  if (n === null) return unavailable;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: OPTIMUS_MONEY_FRACTION_DIGITS,
    maximumFractionDigits: OPTIMUS_MONEY_FRACTION_DIGITS,
  })}`;
}

/* Signed money for variance deltas: the sign leads, the shape is unchanged. */
export function formatOptimusSignedMoney(value, { unavailable = OPTIMUS_MONEY_UNAVAILABLE } = {}) {
  const n = toFiniteNumber(value);
  if (n === null) return unavailable;
  const base = formatOptimusMoney(Math.abs(n), { unavailable });
  if (n > 0) return `+${base}`;
  if (n < 0) return `-${base}`;
  return base;
}

/*
 * Counts and rates are NOT money and never grow a "$". They live here beside
 * the money formatter so a component reaching for "how do I display this"
 * finds the whole vocabulary in one place instead of inventing half of it.
 */
export function formatOptimusCount(value, { unavailable = "—" } = {}) {
  const n = toFiniteNumber(value);
  if (n === null) return unavailable;
  return Math.round(n).toLocaleString("en-US");
}

export function formatOptimusPercent(value, { digits = 2, unavailable = "— %" } = {}) {
  const n = toFiniteNumber(value);
  if (n === null) return unavailable;
  return `${n.toFixed(digits)} %`;
}
